import {
  Command,
  CommandGroup,
  CommandItem,
  CommandInput,
  CommandList,
} from "@/components/ui/command";
import { Spinner } from "@/components/ui/spinner";
import { CornerDownRight, CornerDownLeft } from "lucide-react";
import {
  Tools,
  ITool,
  ICollection,
  IAutocompleteItem,
  IMcpTool,
} from "@/types";
import { cn } from "@/utils/cn";
import ToolIcon from "./agent-tool-icons";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useRef } from "react";

interface IMenuProps {
  isOpen: boolean;
  tools: Tools;
  id?: string;
  onOpenChange: (open: boolean, restoreFocus?: boolean) => void;
  onSelectedIndexChange?: (index: number) => void;
  isError?: boolean;
  onRetry?: () => void;
  autoFocusSearch?: boolean;
  isItemSelectable?: (item: { id: string; selectable?: boolean }) => boolean;
  position: { top?: number; left: number; bottom?: number };
  onSelect: (tool: ITool) => void;
  onCollectionSelect: (
    collection: ICollection | IAutocompleteItem | IMcpTool,
  ) => void;
  menuType: "tools" | "collections";
  collections?: Array<ICollection | IAutocompleteItem | IMcpTool>;
  isLoading: boolean;
  selectedIndex: number;
  selectedTool?: ITool;
  inline?: boolean;
}

export const MenuSelection = ({
  isOpen,
  tools,
  position,
  onSelect,
  menuType = "tools",
  collections,
  onCollectionSelect,
  isLoading,
  selectedIndex,
  searchQuery,
  onSearchChange,
  selectedTool,
  inline = false,
  id,
  onOpenChange,
  onSelectedIndexChange,
  isError = false,
  onRetry,
  autoFocusSearch = false,
  isItemSelectable,
}: IMenuProps & {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const tabNavigation = useRef(false);
  const selectedValue =
    menuType === "tools"
      ? tools[selectedIndex]?.id
      : collections?.[selectedIndex]?.id;
  return isOpen ? (
    <div
      id={id}
      role="dialog"
      aria-label={t("playground.availableTools")}
      onBlur={(event) => {
        if (
          tabNavigation.current &&
          !event.currentTarget.contains(event.relatedTarget as Node | null)
        )
          onOpenChange(false);
        tabNavigation.current = false;
      }}
      onKeyDownCapture={(event) => {
        if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229)
          return;
        tabNavigation.current = event.key === "Tab";
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onOpenChange(false, true);
        }
      }}
      style={{
        ...(inline
          ? {}
          : {
              top: position.top,
              // The editor supplies viewport DOMRect coordinates in both directions.
              left: position.left,
              bottom: position.bottom,
            }),
        scrollbarColor: "var(--primary) transparent",
      }}
      contentEditable={false}
      className={cn(
        "w-[209px] max-w-[95vw] z-[1500] overflow-y-auto py-2 px-1 rounded-2xl border-accent border-solid border-[1px] bg-[var(--card-background)] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:bg-transparent",
        inline ? "relative mt-2" : "fixed",
        {
          "flex items-center justify-center h-32": isLoading,
        },
      )}
    >
      <Command
        label={t("playground.searchPlaceholder")}
        value={selectedValue}
        shouldFilter={false}
        onValueChange={(value) => {
          const items = menuType === "tools" ? tools : collections;
          const index = items?.findIndex((item) => item.id === value) ?? -1;
          if (index >= 0) onSelectedIndexChange?.(index);
        }}
      >
        <CommandInput
          autoFocus={autoFocusSearch}
          aria-label={t("playground.searchPlaceholder")}
          value={searchQuery}
          onValueChange={onSearchChange}
          placeholder={t("playground.searchPlaceholder")}
          className="h-8 border-b-[var(--border)] text-sm focus:outline-hidden"
        />
        <CommandList>
          {isError ? (
            <div className="px-2 py-3 text-sm">
              <p role="alert">{t("playground.toolsLoadError")}</p>
              <Button
                type="button"
                variant="ghost"
                className="mt-2 h-8"
                onClick={onRetry}
              >
                {t("common.retry")}
              </Button>
            </div>
          ) : !isLoading ? (
            <>
              {(menuType === "tools"
                ? tools.length
                : (collections?.length ?? 0)) === 0 && (
                <p
                  role="status"
                  className="px-2 py-3 text-sm text-muted-foreground"
                >
                  {t("playground.noToolsResults")}
                </p>
              )}
              <CommandGroup
                heading={selectedTool?.name ?? t("playground.toolLabel")}
              >
                {menuType === "tools"
                  ? tools.map((tool) => (
                      <CommandItem
                        disabled={
                          isItemSelectable ? !isItemSelectable(tool) : false
                        }
                        value={tool.id}
                        onSelect={() => {
                          onSelect(tool);
                        }}
                        key={tool.id}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          <ToolIcon
                            name={
                              tool?.icon ||
                              (tool.type === "mcp" ? "blocks" : "wrench")
                            }
                            className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2"
                          />
                          {tool.name}
                        </div>
                        {(tool.has_autocomplete || tool.type === "mcp") &&
                          (isRTL ? <CornerDownLeft /> : <CornerDownRight />)}
                      </CommandItem>
                    ))
                  : collections?.map((collection) => {
                      const subtext =
                        "source" in collection && collection.source
                          ? collection.source
                          : undefined;

                      return (
                        <CommandItem
                          disabled={
                            isItemSelectable
                              ? !isItemSelectable(collection)
                              : false
                          }
                          value={collection.id}
                          onSelect={() => {
                            onCollectionSelect(collection);
                          }}
                          key={collection.id}
                          className="flex items-center gap-2"
                        >
                          <ToolIcon
                            name={
                              selectedTool?.icon ||
                              ((collection as any).type === "collection"
                                ? "library-big"
                                : (collection as any).type === "document"
                                  ? "file-text"
                                  : (collection as any).type === "flow"
                                    ? "workflow"
                                    : selectedTool?.type === "mcp"
                                      ? (collection as any).icon || "blocks"
                                      : "wrench")
                            }
                            className="w-4 h-4"
                          />
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium">
                              {collection.name}
                            </div>
                            {subtext ? (
                              <div className="truncate text-xs text-muted-foreground">
                                {subtext}
                              </div>
                            ) : null}
                          </div>
                        </CommandItem>
                      );
                    })}
              </CommandGroup>
            </>
          ) : (
            <div className="flex items-center justify-center py-4">
              <Spinner className="motion-reduce:animate-none" />
            </div>
          )}
        </CommandList>
      </Command>
    </div>
  ) : null;
};
